import {useState, useRef} from 'react';
import {useDispatch} from 'react-redux';
import ContentSection from '@frontend/ui-kit/Components/ContentSection';
import Row from '@frontend/ui-kit/Components/Row';
import Column from '@frontend/ui-kit/Components/Column';
import Heading, {HEADING_TYPES} from '@frontend/ui-kit/Components/Heading';
import Button, {BUTTON_TYPES} from '@frontend/ui-kit/Components/Button';
import Link from '@frontend/ui-kit/Components/Link';
import Text from '@frontend/ui-kit/Components/Text';
import Input from '@frontend/ui-kit/Components/Input';
import GlobalErrorMessage from '@frontend/ui-kit/Components/GlobalErrorMessage';
import {requestCompany} from '../../../actions/companies';
import analyticsService, {ANALYTICS_EVENTS} from '../../../analyticsService';
import {EAP_WEBSITE} from '../../../constants';
import {AppDispatch} from '../../../store';
import {gettext} from '../../../translationService';
import './index.scss';

const LoginForm = (): JSX.Element => {
    const dispatch = useDispatch<AppDispatch>();
    const [companyName, setCompanyName] = useState<string>('');
    const [isError, setError] = useState<boolean>(false);
    const gunUsernameRef = useRef<HTMLInputElement>(null);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();

        if (!companyName.trim()) {
            setError(true);
            return;
        }

        const {company, isSuccess, statusCode} = await dispatch(requestCompany(companyName));

        analyticsService.track(
            ANALYTICS_EVENTS.eapPortal.formSubmitted,
            isSuccess ? {has_login: !!company?.login} : {status_code: statusCode}
        );

        if (!company?.login) {
            setError(true);
            return;
        }

        setError(false);
        if (gunUsernameRef.current) {
            gunUsernameRef.current.value = company.login;
        }
        // @ts-ignore
        event.target.submit();
    };
    return (
        <ContentSection className='eap-login-form'>
            <form className='mb-16' method='POST' action={EAP_WEBSITE} onSubmit={onSubmit}>
                <input ref={gunUsernameRef} data-testid='gun_username' name='gun_username' type='hidden' defaultValue=''/>

                <Heading type={HEADING_TYPES['1']} className='mb-12'>
                    {gettext('HealthJoy EAP offers a wide range of services to help employees and their family members when life presents challenges.')}
                </Heading>

                <Text className='mb-12'>
                    {gettext('Log in to the site by typing your username. Your username is your company name')}
                </Text>

                <Row bottom='xs'>
                    <Column xs={12} md={6}>
                        <Input wrapperClassName='eap-login-form__input'
                            name='company_name'
                            data-testid='company_name'
                            label={gettext('Company Name')}
                            placeholder={gettext('Enter your company name')}
                            value={companyName}
                            onChange={event => setCompanyName(event.target.value)}/>
                    </Column>

                    <Column xs={12} md={6}>
                        <Button type={BUTTON_TYPES.primary}
                            className='eap-login-form__login-button'
                            data-testid='login-button'
                            isSubmit>
                            {gettext('Login')}
                        </Button>
                    </Column>
                </Row>

                {isError && (
                    <GlobalErrorMessage>
                        {gettext('Company name you entered does not exist, please try again')}
                    </GlobalErrorMessage>
                )}
            </form>

            <Text className='mb-12'>
                {gettext('Having someone to turn to makes all the difference. Your employer has chosen HealthJoy EAP as your Employee Assistance Program. This gives you immediate access to expert content and comprehensive tools to assist you in every aspect of your life, all in a secure, confidential and easy-to-use format.')}
            </Text>
            <Text>
                {gettext('To access your EAP services call')}
                <Link href='tel:(888) 731-3327' className='ml-2 mr-2 eap-login-form__phone-link'>
                    (888) 731-3327.
                </Link>
                {gettext('Clinicians are available to help 24 hours a day, 7 days a week.')}
            </Text>
        </ContentSection>
    );
};

export {LoginForm as TestableLoginForm};
export default LoginForm;
